// src/pages/orders/OrderHistory.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Fade,
  Badge,
  Avatar,
  useTheme,
  alpha,
  Pagination,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
  Stack,
  Divider,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Popover,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterAlt as FilterAltIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  TableRestaurant as TableRestaurantIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  LocalDining as FoodIcon,
  LocalDrink as DrinkIcon,
  DateRange as DateRangeIcon,
  Close as CloseIcon,
  AttachMoney as AttachMoneyIcon,
  ReceiptLong as ReceiptLongIcon,
  Today as TodayIcon,
} from '@mui/icons-material';

// Import components
import Header from '../../../components/RestaurantPOS/waiters/Header';
import Footer from '../../../components/RestaurantPOS/waiters/Footer';
import Preloader from '../../../theme/Preloader';

// Import styles
import '../../../styles/Waiters.css';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const ITEMS_PER_PAGE = 10;

// In OrderHistory.jsx, update location config
const LOCATIONS = [
  { id: 5, name: 'African', color: '#2E7D32', icon: '🌍' },
  { id: 3, name: 'Continental', color: '#1565C0', icon: '🌐' },
  { id: 4, name: 'Lounge', color: '#6A1B9A', icon: '🍸' },
];



// Status configuration
const STATUS_CONFIG = {
  PENDING: { label: 'Pending', color: '#FF9800', icon: <PendingIcon /> },
  IN_PROGRESS: { label: 'In Progress', color: '#2196F3', icon: <ScheduleIcon /> },
  READY: { label: 'Ready', color: '#4CAF50', icon: <CheckCircleIcon /> },
  COMPLETED: { label: 'Completed', color: '#2E7D32', icon: <CheckCircleIcon /> },
  CANCELLED: { label: 'Cancelled', color: '#D32F2F', icon: <CancelIcon /> },
};

// Payment status config
const PAYMENT_STATUS_CONFIG = {
  UNPAID: { label: 'Unpaid', color: '#F44336' },
  PAID: { label: 'Paid', color: '#4CAF50' },
  PARTIAL: { label: 'Partial', color: '#FF9800' },
  REFUNDED: { label: 'Refunded', color: '#9E9E9E' },
};

// Order type config
const ORDER_TYPE_CONFIG = {
  DINE_IN: { label: 'Dine In', icon: <TableRestaurantIcon /> },
  TAKEAWAY: { label: 'Takeaway', icon: <PeopleIcon /> },
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  if (!amount && amount !== 0) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const formatDateInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForAPI = (date) => {
  if (!date) return null;
  const d = new Date(date);
  // Use UTC to avoid timezone offset issues
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getStatusColor = (status) => {
  return STATUS_CONFIG[status]?.color || '#9E9E9E';
};

const getStatusLabel = (status) => {
  return STATUS_CONFIG[status]?.label || status || 'Unknown';
};

const getPaymentStatusColor = (status) => {
  return PAYMENT_STATUS_CONFIG[status]?.color || '#9E9E9E';
};

const getPaymentStatusLabel = (status) => {
  return PAYMENT_STATUS_CONFIG[status]?.label || status || 'Unknown';
};

const getOrderTypeLabel = (type) => {
  return ORDER_TYPE_CONFIG[type]?.label || type || 'Unknown';
};

const getOrderTypeIcon = (type) => {
  return ORDER_TYPE_CONFIG[type]?.icon || <TableRestaurantIcon />;
};

// ============================================
// TOKEN & AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[OrderHistory] Error checking token expiry:', err);
    return true;
  }
};

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[OrderHistory] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[OrderHistory] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[OrderHistory] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[OrderHistory] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/restaurantAuth';
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[OrderHistory] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  // Ensure proper URL formatting with trailing slash for GET requests
  let url = `${FULL_API_URL}${endpoint}`;
  // If endpoint doesn't end with ? and doesn't have a trailing slash, add it
  if (!endpoint.includes('?') && !url.endsWith('/')) {
    url = url + '/';
  }
  
  console.debug(`[OrderHistory] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      console.debug('[OrderHistory] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[OrderHistory] API Error:', err.message);
    throw err;
  }
};

// ============================================
// CUSTOM DATE RANGE PICKER COMPONENT
// ============================================

const CustomDateRangePicker = React.memo(({ dateRange, onDateRangeChange, locationColor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(dateRange.startDate || null);
  const [tempEndDate, setTempEndDate] = useState(dateRange.endDate || null);

  // Update temp dates when dateRange prop changes
  useEffect(() => {
    setTempStartDate(dateRange.startDate || null);
    setTempEndDate(dateRange.endDate || null);
  }, [dateRange]);

  const handleApply = () => {
    onDateRangeChange({ startDate: tempStartDate, endDate: tempEndDate });
    setIsOpen(false);
  };

  const handleReset = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    onDateRangeChange({ startDate: null, endDate: null });
    setIsOpen(false);
  };

  const handleToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);
    setTempStartDate(today);
    setTempEndDate(todayEnd);
    onDateRangeChange({ startDate: today, endDate: todayEnd });
    setIsOpen(false);
  };

  const handleQuickRange = (days) => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    let start = new Date();
    start.setHours(0, 0, 0, 0);
    if (days === 0) {
      handleToday();
      return;
    } else {
      start.setDate(end.getDate() - days);
      start.setHours(0, 0, 0, 0);
    }
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const handleQuickMonth = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const hasDates = dateRange.startDate || dateRange.endDate;
  const isToday = dateRange.startDate && dateRange.endDate && 
    new Date(dateRange.startDate).toDateString() === new Date().toDateString() &&
    new Date(dateRange.endDate).toDateString() === new Date().toDateString();

  const quickRanges = [
    { label: 'Today', days: 'today', icon: <TodayIcon sx={{ fontSize: 16 }} /> },
    { label: 'Yesterday', days: 1 },
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'This Month', days: 'month' },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant={hasDates ? 'contained' : 'outlined'}
          size="medium"
          onClick={() => {
            setTempStartDate(dateRange.startDate || null);
            setTempEndDate(dateRange.endDate || null);
            setIsOpen(true);
          }}
          startIcon={<DateRangeIcon />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            ...(hasDates && {
              bgcolor: locationColor,
              '&:hover': { bgcolor: locationColor, opacity: 0.9 },
            }),
            ...(!hasDates && {
              borderColor: alpha(locationColor, 0.3),
              color: locationColor,
            }),
          }}
        >
          {hasDates ? (
            isToday ? 'Today' : `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
          ) : (
            'Select Date Range'
          )}
        </Button>

        {hasDates && (
          <IconButton
            size="small"
            onClick={() => {
              onDateRangeChange({ startDate: null, endDate: null });
            }}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Date Range Dialog */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2,
            maxWidth: '480px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            Select Date Range
          </Typography>
          <IconButton onClick={() => setIsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Quick Select */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: 'text.secondary' }}>
              Quick Select
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quickRanges.map((range, index) => (
                <Button
                  key={index}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (range.days === 'today') {
                      handleToday();
                    } else if (range.days === 'month') {
                      handleQuickMonth();
                    } else {
                      handleQuickRange(range.days);
                    }
                  }}
                  startIcon={range.icon}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderColor: alpha(locationColor, 0.2),
                    '&:hover': { borderColor: locationColor },
                  }}
                >
                  {range.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Custom Date Inputs */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: 'text.secondary' }}>
            Custom Range
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={tempStartDate ? formatDateInput(tempStartDate) : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const date = new Date(value + 'T00:00:00');
                  setTempStartDate(date);
                } else {
                  setTempStartDate(null);
                }
              }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                }
              }}
            />
            <TextField
              label="End Date"
              type="date"
              value={tempEndDate ? formatDateInput(tempEndDate) : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const date = new Date(value + 'T00:00:00');
                  setTempEndDate(date);
                } else {
                  setTempEndDate(null);
                }
              }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                }
              }}
            />
          </Box>

          {/* Validation Message */}
          {tempStartDate && tempEndDate && tempStartDate > tempEndDate && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
              Start date must be before end date
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={handleReset}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
            }}
          >
            Clear
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={tempStartDate && tempEndDate && tempStartDate > tempEndDate}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              bgcolor: locationColor,
              '&:hover': { bgcolor: locationColor, opacity: 0.9 },
              '&:disabled': { bgcolor: 'grey.300' },
            }}
          >
            Apply Range
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

// ============================================
// STATS CARDS COMPONENT
// ============================================

const StatsCards = React.memo(({ stats, locationColor, isMobile }) => {
  const theme = useTheme();
  
  const statItems = [
    {
      label: 'Total Orders',
      value: stats.totalOrders || 0,
      icon: <ReceiptIcon />,
      color: locationColor,
    },
    {
      label: 'Total Revenue',
      value: formatCurrency(stats.totalRevenue || 0),
      icon: <AttachMoneyIcon />,
      color: '#2E7D32',
    },
    {
      label: 'Completed',
      value: stats.completed || 0,
      icon: <CheckCircleIcon />,
      color: '#2E7D32',
    },
    {
      label: 'Pending',
      value: stats.pending || 0,
      icon: <PendingIcon />,
      color: '#FF9800',
    },
    {
      label: 'Cancelled',
      value: stats.cancelled || 0,
      icon: <CancelIcon />,
      color: '#D32F2F',
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {statItems.map((stat, index) => (
        <Grid item xs={6} sm={6} md={isMobile ? 6 : 2.4} key={index}>
          <Card
            elevation={0}
            sx={{
              borderRadius: '14px',
              border: `1px solid ${alpha(stat.color, 0.15)}`,
              bgcolor: alpha(stat.color, 0.04),
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: theme.shadows[4],
              },
              height: '100%',
            }}
          >
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(stat.color, 0.12),
                    color: stat.color,
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                  }}
                >
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      color: stat.color,
                      lineHeight: 1.2,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
});

// ============================================
// FILTER BAR COMPONENT
// ============================================

const FilterBar = React.memo(({
  filters,
  onFilterChange,
  onSearch,
  onRefresh,
  isFetching,
  locationColor,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempFilters, setTempFilters] = useState(filters);
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const searchTimeoutRef = useRef(null);

  // Update searchValue when filters.search changes from parent
  useEffect(() => {
    setSearchValue(filters.search || '');
  }, [filters.search]);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
    setTempFilters(filters);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleApplyFilters = () => {
    onFilterChange(tempFilters);
    handleFilterClose();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      status: 'ALL',
      paymentStatus: 'ALL',
      orderType: 'ALL',
     
      search: searchValue,
    };
    setTempFilters(resetFilters);
    onFilterChange(resetFilters);
    handleFilterClose();
  };

  const handleTempChange = (field, value) => {
    setTempFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      onSearch(value);
    }, 400);
  };

  const filterCount = Object.values(filters).filter(v => 
    v !== 'ALL' && v !== '' && v !== null && v !== undefined
  ).length;

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: 2,
      mb: 3,
      flexWrap: 'wrap',
      alignItems: 'center',
    }}>
      <TextField
        size="small"
        placeholder="Search by order number, customer, or table..."
        value={searchValue}
        onChange={handleSearchChange}
        sx={{
          flex: 1,
          minWidth: '200px',
          maxWidth: { xs: '100%', sm: '400px' },
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            bgcolor: 'white',
            '& fieldset': { borderColor: alpha(locationColor, 0.2) },
            '&:hover fieldset': { borderColor: locationColor },
            '&.Mui-focused fieldset': { borderColor: locationColor },
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#94a3b8' }} />
            </InputAdornment>
          ),
          endAdornment: searchValue && (
            <InputAdornment position="end">
              <IconButton 
                size="small" 
                onClick={() => {
                  setSearchValue('');
                  onSearch('');
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="medium"
          onClick={handleFilterClick}
          startIcon={<FilterAltIcon />}
          endIcon={filterCount > 0 ? <Badge badgeContent={filterCount} color="primary" /> : null}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            borderColor: alpha(locationColor, 0.3),
            color: locationColor,
            '&:hover': {
              borderColor: locationColor,
              bgcolor: alpha(locationColor, 0.05),
            },
          }}
        >
          Filters
        </Button>

        <Tooltip title="Refresh Data">
          <IconButton
            onClick={onRefresh}
            disabled={isFetching}
            sx={{
              bgcolor: alpha(locationColor, 0.08),
              '&:hover': { bgcolor: alpha(locationColor, 0.15) },
              borderRadius: '10px',
              width: 40,
              height: 40,
            }}
          >
            {isFetching ? <CircularProgress size={22} /> : <RefreshIcon sx={{ color: locationColor }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2,
            minWidth: { xs: 'calc(100vw - 32px)', sm: '360px' },
            maxWidth: '400px',
            boxShadow: (theme) => theme.shadows[12],
          }
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Filter Orders
        </Typography>

        <Stack spacing={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel>Order Status</InputLabel>
            <Select
              value={tempFilters.status || 'ALL'}
              onChange={(e) => handleTempChange('status', e.target.value)}
              label="Order Status"
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="ALL">All Status</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {config.icon}
                    <span>{config.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={tempFilters.paymentStatus || 'ALL'}
              onChange={(e) => handleTempChange('paymentStatus', e.target.value)}
              label="Payment Status"
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="ALL">All Payment Status</MenuItem>
              {Object.entries(PAYMENT_STATUS_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ color: config.color }}>●</span>
                    <span>{config.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Order Type</InputLabel>
            <Select
              value={tempFilters.orderType || 'ALL'}
              onChange={(e) => handleTempChange('orderType', e.target.value)}
              label="Order Type"
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="ALL">All Types</MenuItem>
              {Object.entries(ORDER_TYPE_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {config.icon}
                    <span>{config.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

         
          <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleResetFilters}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Reset
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: locationColor,
                '&:hover': { bgcolor: locationColor, opacity: 0.9 },
              }}
            >
              Apply
            </Button>
          </Box>
        </Stack>
      </Popover>
    </Box>
  );
});

// ============================================
// ORDER CARD COMPONENT (Mobile/Tablet)
// ============================================

const OrderCard = React.memo(({ order, locationColor, onViewDetails }) => {
  const theme = useTheme();
  let items = [];
  try {
    items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
  } catch (e) { items = []; }

  const itemCount = items.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const statusColor = getStatusColor(order.order_status);
  const statusLabel = getStatusLabel(order.order_status);
  const paymentStatusColor = getPaymentStatusColor(order.payment_status);
  const paymentStatusLabel = getPaymentStatusLabel(order.payment_status);
  const orderTypeLabel = getOrderTypeLabel(order.order_type);
  const OrderTypeIcon = getOrderTypeIcon(order.order_type);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: `1px solid ${alpha('#000', 0.06)}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha(locationColor, 0.3),
          boxShadow: theme.shadows[3],
        },
        mb: 2,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
              {order.order_number}
            </Typography>
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                bgcolor: alpha(statusColor, 0.12),
                color: statusColor,
                fontWeight: 700,
                fontSize: '0.65rem',
                height: 24,
                border: `1px solid ${alpha(statusColor, 0.2)}`,
              }}
            />
            <Chip
              label={paymentStatusLabel}
              size="small"
              sx={{
                bgcolor: alpha(paymentStatusColor, 0.12),
                color: paymentStatusColor,
                fontWeight: 700,
                fontSize: '0.6rem',
                height: 22,
                border: `1px solid ${alpha(paymentStatusColor, 0.2)}`,
              }}
            />
          </Box>
          <Typography variant="h6" fontWeight={700} color={locationColor} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
            {formatCurrency(order.total_amount)}
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 1.5,
        }}>
          <Chip
            icon={OrderTypeIcon}
            label={orderTypeLabel}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', fontWeight: 600 }}
          />
          {order.table_name && (
            <Chip
              icon={<TableRestaurantIcon sx={{ fontSize: 14 }} />}
              label={order.table_name}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', fontWeight: 600 }}
            />
          )}
          <Chip
            icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
            label={formatTime(order.order_time)}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', fontWeight: 600 }}
          />
          {order.waiter_name && (
            <Chip
              icon={<PeopleIcon sx={{ fontSize: 14 }} />}
              label={order.waiter_name}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', fontWeight: 600 }}
            />
          )}
          {order.location_name && (
            <Chip
              label={order.location_name}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', fontWeight: 600 }}
            />
          )}
        </Box>

        {items.length > 0 && (
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mb: 1.5,
            pt: 1.5,
            borderTop: `1px dashed ${alpha('#000', 0.06)}`,
          }}>
            {items.slice(0, 4).map((item, idx) => (
              <Chip
                key={idx}
                label={`${item.name} ×${item.quantity || 1}`}
                size="small"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: alpha(locationColor, 0.06),
                  borderRadius: '6px',
                  height: 26,
                  '& .MuiChip-label': { px: 1.5 },
                }}
                icon={item.main_category === 'Food' ? <FoodIcon sx={{ fontSize: 14 }} /> : <DrinkIcon sx={{ fontSize: 14 }} />}
              />
            ))}
            {items.length > 4 && (
              <Chip
                label={`+${items.length - 4} more`}
                size="small"
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: alpha('#000', 0.04),
                  height: 26,
                }}
              />
            )}
          </Box>
        )}

        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          pt: 1.5,
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
        }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onViewDetails(order)}
            startIcon={<VisibilityIcon fontSize="small" />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              borderColor: alpha(locationColor, 0.3),
              color: locationColor,
              '&:hover': { borderColor: locationColor, bgcolor: alpha(locationColor, 0.05) },
            }}
          >
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
});

// ============================================
// ORDER TABLE ROW COMPONENT
// ============================================

const OrderTableRow = React.memo(({ order, locationColor, onViewDetails }) => {
  let items = [];
  try {
    items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
  } catch (e) { items = []; }

  const itemCount = items.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const statusColor = getStatusColor(order.order_status);
  const statusLabel = getStatusLabel(order.order_status);
  const paymentStatusColor = getPaymentStatusColor(order.payment_status);
  const paymentStatusLabel = getPaymentStatusLabel(order.payment_status);

  return (
    <TableRow
      hover
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: alpha(locationColor, 0.02) },
        transition: 'background-color 0.15s ease',
      }}
      onClick={() => onViewDetails(order)}
    >
      <TableCell>
        <Typography variant="body2" fontWeight={700}>
          {order.order_number}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTime(order.order_time)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {getOrderTypeLabel(order.order_type)}
        </Typography>
        {order.table_name && (
          <Typography variant="caption" color="text.secondary" display="block">
            {order.table_name}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {order.waiter_name || 'N/A'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {itemCount} items
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            bgcolor: alpha(statusColor, 0.12),
            color: statusColor,
            fontWeight: 700,
            fontSize: '0.65rem',
            height: 24,
            border: `1px solid ${alpha(statusColor, 0.2)}`,
          }}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={paymentStatusLabel}
          size="small"
          sx={{
            bgcolor: alpha(paymentStatusColor, 0.12),
            color: paymentStatusColor,
            fontWeight: 700,
            fontSize: '0.6rem',
            height: 22,
            border: `1px solid ${alpha(paymentStatusColor, 0.2)}`,
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="h6" fontWeight={700} color={locationColor} sx={{ fontSize: '1rem' }}>
          {formatCurrency(order.total_amount)}
        </Typography>
      </TableCell>
    </TableRow>
  );
});

// ============================================
// ORDER DETAIL DIALOG COMPONENT
// ============================================

const OrderDetailDialog = React.memo(({ open, order, onClose, locationColor }) => {
  // Move the useCallback to the top level, before any conditional returns
  const handlePrintReceipt = useCallback(() => {
    if (!order) return;
    
    // Create a print window
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) {
      alert('Please allow popups to print receipts');
      return;
    }
    
    let items = [];
    try {
      items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
    } catch (e) { items = []; }

    const statusColor = getStatusColor(order.order_status);
    const statusLabel = getStatusLabel(order.order_status);
    const paymentStatusColor = getPaymentStatusColor(order.payment_status);
    const paymentStatusLabel = getPaymentStatusLabel(order.payment_status);
    const orderTypeLabel = getOrderTypeLabel(order.order_type);
    
    // Generate receipt HTML with sharp, bold design
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${order.order_number}</title>
        <style>
          * { 
            margin: 0; 
            padding: 0; 
            box-sizing: border-box; 
          }
          body {
            font-family: 'Courier New', Courier, monospace;
            padding: 20px;
            max-width: 380px;
            margin: 0 auto;
            background: #ffffff;
            color: #000000;
            font-weight: 600;
          }
          .receipt-container {
            background: #ffffff;
            padding: 15px;
            border: 2px solid #000;
            border-radius: 4px;
          }
          .receipt-header {
            text-align: center;
            border-bottom: 3px double #000;
            padding-bottom: 15px;
            margin-bottom: 15px;
          }
          .receipt-header h2 {
            font-size: 22px;
            font-weight: 900;
            letter-spacing: 2px;
            text-transform: uppercase;
            color: #000;
            margin-bottom: 5px;
          }
          .receipt-header .order-number {
            font-size: 18px;
            font-weight: 900;
            color: #000;
            letter-spacing: 1px;
          }
          .receipt-header .detail {
            font-size: 13px;
            font-weight: 700;
            color: #000;
            margin: 3px 0;
          }
          .receipt-header .detail-label {
            color: #555;
            font-weight: 600;
          }
          .status-section {
            margin-top: 10px;
            display: flex;
            justify-content: center;
            gap: 8px;
            flex-wrap: wrap;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 14px;
            border-radius: 3px;
            font-size: 12px;
            font-weight: 900;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            border: 2px solid #000;
          }
          .status-COMPLETED { background: #000; color: #fff; }
          .status-PENDING { background: #FF9800; color: #000; }
          .status-CANCELLED { background: #D32F2F; color: #fff; }
          .status-IN_PROGRESS { background: #2196F3; color: #fff; }
          .status-READY { background: #4CAF50; color: #fff; }
          
          .payment-badge {
            display: inline-block;
            padding: 4px 14px;
            border-radius: 3px;
            font-size: 12px;
            font-weight: 900;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            border: 2px solid #000;
          }
          .payment-PAID { background: #000; color: #fff; }
          .payment-UNPAID { background: #fff; color: #000; }
          .payment-PARTIAL { background: #FF9800; color: #000; }
          
          .items-section {
            margin: 15px 0;
            border-top: 2px solid #000;
            padding-top: 10px;
          }
          .item-header {
            display: flex;
            justify-content: space-between;
            font-weight: 900;
            font-size: 14px;
            padding: 5px 0;
            border-bottom: 2px solid #000;
            margin-bottom: 5px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            background: #f5f5f5;
            padding: 8px 5px;
          }
          .item-row {
            display: flex;
            justify-content: space-between;
            padding: 6px 5px;
            border-bottom: 1px solid #ddd;
            font-size: 14px;
            font-weight: 700;
          }
          .item-row:last-child {
            border-bottom: none;
          }
          .item-name { 
            flex: 1; 
            font-weight: 700;
            color: #000;
          }
          .item-qty { 
            margin: 0 10px; 
            color: #000;
            font-weight: 700;
            min-width: 30px;
            text-align: center;
          }
          .item-price { 
            font-weight: 900;
            color: #000;
            min-width: 80px;
            text-align: right;
          }
          .item-notes {
            font-size: 11px;
            color: #555;
            font-weight: 600;
            font-style: italic;
            margin-top: 2px;
          }
          .totals-section {
            margin-top: 15px;
            border-top: 3px double #000;
            padding-top: 12px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 14px;
            font-weight: 700;
          }
          .total-row .label {
            font-weight: 700;
            color: #000;
          }
          .total-row .value {
            font-weight: 700;
            color: #000;
          }
          .grand-total {
            font-size: 20px;
            font-weight: 900;
            border-top: 3px solid #000;
            padding-top: 10px;
            margin-top: 8px;
            background: #f5f5f5;
            padding: 10px 5px;
          }
          .grand-total .label {
            font-weight: 900;
            color: #000;
            font-size: 20px;
          }
          .grand-total .value {
            font-weight: 900;
            color: #000;
            font-size: 20px;
          }
          .footer {
            text-align: center;
            margin-top: 20px;
            padding-top: 15px;
            border-top: 3px double #000;
            font-size: 13px;
            font-weight: 700;
            color: #000;
          }
          .footer p {
            margin: 3px 0;
          }
          .footer .thankyou {
            font-size: 16px;
            font-weight: 900;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .print-btn {
            display: block;
            margin: 20px auto;
            padding: 12px 40px;
            font-size: 16px;
            font-weight: 900;
            cursor: pointer;
            background: #000;
            color: #fff;
            border: none;
            border-radius: 4px;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .print-btn:hover { 
            background: #333; 
          }
          .close-btn {
            display: block;
            margin: 10px auto;
            padding: 8px 30px;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
            background: #fff;
            color: #000;
            border: 2px solid #000;
            border-radius: 4px;
          }
          .close-btn:hover {
            background: #f5f5f5;
          }
          @media print {
            .no-print { display: none !important; }
            body { 
              padding: 10px; 
              background: #fff;
            }
            .receipt-container {
              border: 2px solid #000;
              padding: 10px;
            }
            .print-btn, .close-btn {
              display: none !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <div class="receipt-header">
            <h2>${order.location_name || 'RESTAURANT'}</h2>
            <div class="order-number"># ${order.order_number}</div>
            <div class="detail">
              <span class="detail-label">Date:</span> ${formatDateTime(order.order_time)}
            </div>
            <div class="detail">
              <span class="detail-label">Type:</span> ${orderTypeLabel}
            </div>
            ${order.table_name ? `
              <div class="detail">
                <span class="detail-label">Table:</span> ${order.table_name}
              </div>
            ` : ''}
            ${order.waiter_name ? `
              <div class="detail">
                <span class="detail-label">Waiter:</span> ${order.waiter_name}
              </div>
            ` : ''}
            ${order.customer_name ? `
              <div class="detail">
                <span class="detail-label">Customer:</span> ${order.customer_name}
              </div>
            ` : ''}
            <div class="status-section">
              <span class="status-badge status-${order.order_status}">${statusLabel}</span>
              <span class="payment-badge payment-${order.payment_status}">${paymentStatusLabel}</span>
            </div>
          </div>

          <div class="items-section">
            <div class="item-header">
              <span>ITEM</span>
              <span>QTY</span>
              <span>AMOUNT</span>
            </div>
            ${items.map(item => `
              <div>
                <div class="item-row">
                  <span class="item-name">${item.name}</span>
                  <span class="item-qty">×${item.quantity || 1}</span>
                  <span class="item-price">${formatCurrency((item.price || 0) * (item.quantity || 1))}</span>
                </div>
                ${item.notes ? `<div class="item-notes">* ${item.notes}</div>` : ''}
              </div>
            `).join('')}
          </div>

          <div class="totals-section">
            <div class="total-row">
              <span class="label">Subtotal</span>
              <span class="value">${formatCurrency(order.subtotal)}</span>
            </div>
            ${order.tax > 0 ? `
              <div class="total-row">
                <span class="label">Tax</span>
                <span class="value">${formatCurrency(order.tax)}</span>
              </div>
            ` : ''}
            ${order.discount > 0 ? `
              <div class="total-row">
                <span class="label">Discount</span>
                <span class="value" style="color:#D32F2F;">-${formatCurrency(order.discount)}</span>
              </div>
            ` : ''}
            <div class="total-row grand-total">
              <span class="label">TOTAL</span>
              <span class="value">${formatCurrency(order.total_amount)}</span>
            </div>
          </div>

          <div class="footer">
            <p class="thankyou">Thank You!</p>
            <p>${order.location_name || ''}</p>
            <p>${new Date().toLocaleString()}</p>
            <p style="font-size:11px;font-weight:600;margin-top:5px;letter-spacing:0.5px;">
              ${order.order_status === 'COMPLETED' ? '✅ Payment Confirmed' : ''}
              ${order.order_status === 'PENDING' ? '⏳ Payment Pending' : ''}
            </p>
          </div>
        </div>
        
        <button class="print-btn no-print" onclick="window.print()">🖨️ PRINT</button>
        <button class="close-btn no-print" onclick="window.close()">✕ CLOSE</button>
      </body>
      </html>
    `;
    
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    
    // Auto-print after content loads
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  }, [order]);

  // Now the conditional return comes AFTER all hooks
  if (!order) return null;

  let items = [];
  try {
    items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
  } catch (e) { items = []; }

  const statusColor = getStatusColor(order.order_status);
  const statusLabel = getStatusLabel(order.order_status);
  const paymentStatusColor = getPaymentStatusColor(order.payment_status);
  const paymentStatusLabel = getPaymentStatusLabel(order.payment_status);
  const orderTypeLabel = getOrderTypeLabel(order.order_type);
  const OrderTypeIcon = getOrderTypeIcon(order.order_type);

  const totalItems = items.reduce((sum, i) => sum + (i.quantity || 1), 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxHeight: '90vh',
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        pb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: '#f8f9fa',
        borderRadius: '20px 20px 0 0',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ReceiptIcon sx={{ color: locationColor, fontSize: 28 }} />
          <Typography variant="h5" component="span" fontWeight={700}>
            {order.order_number}
          </Typography>
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              bgcolor: alpha(statusColor, 0.12),
              color: statusColor,
              fontWeight: 700,
            }}
          />
          <Chip
            label={paymentStatusLabel}
            size="small"
            sx={{
              bgcolor: alpha(paymentStatusColor, 0.12),
              color: paymentStatusColor,
              fontWeight: 700,
            }}
          />
        </Box>
        <IconButton onClick={onClose} sx={{ bgcolor: alpha('#000', 0.04) }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        <Box sx={{
          p: 3,
          bgcolor: alpha(locationColor, 0.02),
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Order Type
            </Typography>
            <Typography variant="body1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {OrderTypeIcon}
              {orderTypeLabel}
            </Typography>
          </Box>
          
          {order.table_name && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                Table
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {order.table_name}
              </Typography>
            </Box>
          )}
          
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Waiter
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {order.waiter_name || 'N/A'}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Order Time
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatDateTime(order.order_time)}
            </Typography>
          </Box>

          {order.customer_name && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                Customer
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {order.customer_name}
                {order.customer_phone && ` (${order.customer_phone})`}
              </Typography>
            </Box>
          )}

          {order.location_name && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                Location
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {order.location_name}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}>
            <Typography variant="h6" fontWeight={700}>
              Order Items ({totalItems})
            </Typography>
          </Box>

          <Box sx={{
            bgcolor: alpha('#000', 0.02),
            borderRadius: '12px',
            overflow: 'hidden',
          }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 80px 100px',
              gap: 1,
              p: 2,
              bgcolor: alpha('#000', 0.04),
              fontWeight: 700,
              fontSize: '0.8rem',
              color: 'text.secondary',
              borderBottom: `1px solid ${alpha('#000', 0.06)}`,
            }}>
              <Typography variant="caption" fontWeight={700}>Item</Typography>
              <Typography variant="caption" fontWeight={700} align="center">Qty</Typography>
              <Typography variant="caption" fontWeight={700} align="right">Amount</Typography>
            </Box>

            {items.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px 100px',
                  gap: 1,
                  p: 2,
                  borderBottom: idx < items.length - 1 ? `1px solid ${alpha('#000', 0.04)}` : 'none',
                  alignItems: 'center',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { bgcolor: alpha(locationColor, 0.02) },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {item.name}
                  </Typography>
                  {item.notes && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                      * {item.notes}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" fontWeight={600} align="center">
                  ×{item.quantity || 1}
                </Typography>
                <Typography variant="body2" fontWeight={700} align="right" color={locationColor}>
                  {formatCurrency((item.price || 0) * (item.quantity || 1))}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{
            mt: 3,
            pt: 2,
            borderTop: `2px solid ${alpha('#000', 0.06)}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.5,
          }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Subtotal
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(order.subtotal)}
              </Typography>
            </Box>
            {order.tax > 0 && (
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Tax
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(order.tax)}
                </Typography>
              </Box>
            )}
            {order.discount > 0 && (
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Discount
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#D32F2F">
                  -{formatCurrency(order.discount)}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 3, pt: 1, borderTop: `1px solid ${alpha('#000', 0.06)}` }}>
              <Typography variant="h6" fontWeight={700}>
                Total
              </Typography>
              <Typography variant="h6" fontWeight={700} color={locationColor}>
                {formatCurrency(order.total_amount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        borderTop: `1px solid ${alpha('#000', 0.06)}`,
        pt: 2,
        px: 3,
        pb: 3,
        gap: 1,
        flexWrap: 'wrap',
      }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '10px',
            px: 4,
          }}
        >
          Close
        </Button>
        {order.order_status !== 'CANCELLED' && (
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrintReceipt}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              bgcolor: locationColor,
              '&:hover': { bgcolor: locationColor, opacity: 0.9 },
            }}
          >
            Print Receipt
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

// ============================================
// MAIN COMPONENT
// ============================================

const OrderHistory = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const locationConfig = LOCATIONS.find(l => 
    l.name.toLowerCase() === locationKey.toLowerCase()
  ) || LOCATIONS[0];
  const LOCATION_COLOR = locationConfig.color;
  const LOCATION_NAME = locationConfig.name;
  const LOCATION_ID = locationConfig.id;

  // ============================================
  // STATE MANAGEMENT
  // ============================================

  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

 
 // Get today's date range by default
const getTodayRange = useCallback(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayEnd = new Date(today);
  todayEnd.setHours(23, 59, 59, 999);
  return { startDate: today, endDate: todayEnd };
}, []);

  const [filters, setFilters] = useState({
    search: '',
    status: 'ALL',
    paymentStatus: 'ALL',
    orderType: 'ALL',
   
  });
  
  // Use today as default to show orders
const [dateRange, setDateRange] = useState(getTodayRange());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const isMountedRef = useRef(true);
  const fetchTimeoutRef = useRef(null);
  const isInitialMountRef = useRef(true);

  // ============================================
  // DATA FETCHING
  // ============================================

// In OrderHistory.jsx, update the fetchOrders function:
const fetchOrders = useCallback(async () => {
  if (!isMountedRef.current) return;
  
  try {
    console.debug(`[OrderHistory] Fetching orders for ${LOCATION_NAME}...`);
    setIsFetching(true);

    const params = new URLSearchParams();
    params.append('limit', '500');
    params.append('skip', '0');

    // Get the current user's ID from localStorage
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const waiterId = currentUser?.id;

    // Always filter by location - use the current location
    const locationId = filters.locationId !== 'ALL' ? filters.locationId : LOCATION_ID;
    if (locationId) {
      params.append('location_id', locationId);
    }

    // Filter by waiter ID to ensure staff only see their own orders
    if (waiterId) {
      params.append('waiter_id', waiterId);
    }

    if (filters.status !== 'ALL') {
      params.append('order_status', filters.status);
    }

    if (filters.paymentStatus !== 'ALL') {
      params.append('payment_status', filters.paymentStatus);
    }

    if (filters.orderType !== 'ALL') {
      params.append('order_type', filters.orderType);
    }

    // ✅ FIX: Format dates properly for API
    if (dateRange.startDate) {
      const dateStr = formatDateForAPI(dateRange.startDate);
      if (dateStr) {
        params.append('date_from', dateStr);
        console.debug(`[OrderHistory] Date from: ${dateStr}`);
      }
    }
    if (dateRange.endDate) {
      const dateStr = formatDateForAPI(dateRange.endDate);
      if (dateStr) {
        params.append('date_to', dateStr);
        console.debug(`[OrderHistory] Date to: ${dateStr}`);
      }
    }

    if (filters.search && filters.search.trim()) {
      params.append('search', filters.search.trim());
    }

    const url = `/orders/history/?${params.toString()}`;
    console.debug('[OrderHistory] Fetching with URL:', url);

    const data = await apiRequest(url);
    console.debug('[OrderHistory] Orders fetched:', data);

    if (isMountedRef.current) {
      let ordersList = [];
      if (data && typeof data === 'object') {
        if (Array.isArray(data.items)) {
          ordersList = data.items;
        } else if (Array.isArray(data)) {
          ordersList = data;
        }
      }

      // ✅ ADD: Additional client-side date filtering to ensure only date range orders are shown
      if (dateRange.startDate && dateRange.endDate) {
        const startStr = formatDateForAPI(dateRange.startDate);
        const endStr = formatDateForAPI(dateRange.endDate);
        
        ordersList = ordersList.filter(order => {
          const orderDate = order.order_date || order.created_date || order.order_time;
          if (!orderDate) return false;
          const orderDateStr = new Date(orderDate).toISOString().split('T')[0];
          return orderDateStr >= startStr && orderDateStr <= endStr;
        });
        
        console.debug(`[OrderHistory] Filtered to ${ordersList.length} orders within date range`);
      }

      console.debug(`[OrderHistory] Parsed ${ordersList.length} orders`);
      
      if (ordersList.length > 0) {
        console.debug('[OrderHistory] First order sample:', ordersList[0]);
      }

      setOrders(ordersList);
      setFilteredOrders(ordersList);

      const totalOrdersCount = ordersList.length;
      const totalRevenue = ordersList
        .filter(o => o.order_status === 'COMPLETED')
        .reduce((sum, o) => sum + (o.total_amount || 0), 0);
      const completed = ordersList.filter(o => o.order_status === 'COMPLETED').length;
      const pending = ordersList.filter(o => o.order_status === 'PENDING').length;
      const cancelled = ordersList.filter(o => o.order_status === 'CANCELLED').length;

      setStats({
        totalOrders: totalOrdersCount,
        totalRevenue: totalRevenue,
        completed,
        pending,
        cancelled,
      });
    }
  } catch (err) {
    console.error('[OrderHistory] Error fetching orders:', err);
    if (isMountedRef.current) {
      setSnackbar({
        open: true,
        message: `Failed to fetch orders: ${err.message}`,
        severity: 'error',
      });
    }
  } finally {
    if (isMountedRef.current) {
      setIsFetching(false);
    }
  }
}, [LOCATION_ID, LOCATION_NAME, filters, dateRange]);

  // ============================================
  // INITIALIZATION
  // ============================================

  useEffect(() => {
    isMountedRef.current = true;

    // Load orders after mount
    const loadData = async () => {
      await fetchOrders();
      if (isMountedRef.current) {
        setIsLoading(false);
        isInitialMountRef.current = false;
      }
    };
    
    loadData();

    return () => {
      isMountedRef.current = false;
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // ============================================
  // RE-FETCH WHEN DEPENDENCIES CHANGE
  // ============================================

  useEffect(() => {
    // Skip initial mount
    if (isInitialMountRef.current) return;
    
    if (isMountedRef.current) {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      fetchTimeoutRef.current = setTimeout(() => {
        fetchOrders();
      }, 300);
    }
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [filters, dateRange, fetchOrders]);

  // ============================================
  // TOKEN AUTO-REFRESH
  // ============================================

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      if (isTokenExpired(token)) {
        await refreshAuthToken();
      }
    };
    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  // ============================================
  // HANDLERS
  // ============================================

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(0);
  }, []);

  const handleSearch = useCallback((value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPage(0);
  }, []);

  const handleDateRangeChange = useCallback((newDateRange) => {
    console.debug('[OrderHistory] Date range changed:', newDateRange);
    setDateRange(newDateRange);
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchOrders();
    setSnackbar({ open: true, message: 'Data refreshed successfully', severity: 'success' });
  }, [fetchOrders]);

  const handleViewDetails = useCallback((order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedOrder(null);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ============================================
  // LOADING STATE
  // ============================================

  if (isLoading) {
    return <Preloader text={`Loading ${LOCATION_NAME} Order History...`} size={70} fullScreen={true} />;
  }

  // ============================================
  // DYNAMIC SIDEBAR
  // ============================================

  let Sidebar;
  try {
    Sidebar = require(`../../../components/RestaurantPOS/waiters/${locationKey}/Sidebar`).default;
  } catch (e) {
    Sidebar = require('../../../components/RestaurantPOS/waiters/african/Sidebar').default;
  }

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <>
      <div className="epos-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <div className="epos-main-layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Sidebar activeMenu="Order History" setActiveMenu={() => {}} />
          
          <div className="epos-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            <Header />

            <Box sx={{
              flex: 1,
              overflow: 'auto',
              p: { xs: 2, sm: 3 },
              bgcolor: '#f5f7fa',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
            }}>
              {/* Header Section */}
            {/* Header Section */}
<Box sx={{
  bgcolor: 'white',
  borderRadius: '16px',
  p: { xs: 2, sm: 3 },
  mb: 3,
  border: `1px solid ${alpha(LOCATION_COLOR, 0.15)}`,
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
}}>
  <Box sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
  }}>
    <Box>
      <Typography variant="h4" sx={{
        color: LOCATION_COLOR,
        fontWeight: 'bold',
        fontSize: { xs: '1.5rem', sm: '2rem' },
      }}>
        📋 Order History
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
        {filteredOrders.length} orders · {LOCATION_NAME}
        {dateRange.startDate && dateRange.endDate && (
          <span style={{ marginLeft: '8px', color: LOCATION_COLOR, fontWeight: 600 }}>
            · {new Date(dateRange.startDate).toDateString() === new Date().toDateString() &&
               new Date(dateRange.endDate).toDateString() === new Date().toDateString() 
              ? 'Today'
              : `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
            }
          </span>
        )}
      </Typography>
    </Box>
    
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Chip
        icon={<span>{locationConfig.icon}</span>}
        label={LOCATION_NAME}
        sx={{
          bgcolor: alpha(LOCATION_COLOR, 0.1),
          color: LOCATION_COLOR,
          fontWeight: 700,
          fontSize: '0.9rem',
          px: 1,
          height: 36,
          '& .MuiChip-label': { fontWeight: 700 },
        }}
      />
    </Box>
  </Box>
</Box>
              {/* Stats Cards */}
              <StatsCards 
                stats={stats} 
                locationColor={LOCATION_COLOR} 
                isMobile={isMobile} 
              />

              {/* Date Range Picker - Custom */}
              <Box sx={{ mb: 3 }}>
                <CustomDateRangePicker
                  dateRange={dateRange}
                  onDateRangeChange={handleDateRangeChange}
                  locationColor={LOCATION_COLOR}
                />
              </Box>

              {/* Filters */}
              <FilterBar
                filters={filters}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onRefresh={handleRefresh}
                isFetching={isFetching}
                locationColor={LOCATION_COLOR}
              />

              {/* Orders Display */}
              {isFetching && filteredOrders.length === 0 ? (
                <Box sx={{ py: 4 }}>
                  <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '12px', mb: 2 }} />
                  <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '12px', mb: 2 }} />
                  <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '12px' }} />
                </Box>
              ) : filteredOrders.length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 8,
                  bgcolor: 'white',
                  borderRadius: '16px',
                  border: `2px dashed ${alpha('#000', 0.08)}`,
                }}>
                  <ReceiptLongIcon sx={{ fontSize: 72, color: 'text.disabled', opacity: 0.3 }} />
                  <Typography variant="h5" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                    No Orders Found
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Try adjusting your filters or date range
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, opacity: 0.7 }}>
                    Date Range: {dateRange.startDate && formatDate(dateRange.startDate)} - {dateRange.endDate && formatDate(dateRange.endDate)}
                    {filters.status !== 'ALL' && ` · Status: ${filters.status}`}
                    {filters.paymentStatus !== 'ALL' && ` · Payment: ${filters.paymentStatus}`}
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Desktop Table View */}
                  {!isMobile && (
                    <Card elevation={0} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: alpha(LOCATION_COLOR, 0.04) }}>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Order #</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Waiter</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Items</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Payment</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedOrders.map((order) => (
                              <OrderTableRow
                                key={order.id}
                                order={order}
                                locationColor={LOCATION_COLOR}
                                onViewDetails={handleViewDetails}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredOrders.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                          borderTop: `1px solid ${alpha('#000', 0.06)}`,
                          '& .MuiTablePagination-select': { fontWeight: 600 },
                          '& .MuiTablePagination-displayedRows': { fontWeight: 600 },
                        }}
                      />
                    </Card>
                  )}

                  {/* Mobile/Tablet Card View */}
                  {(isMobile || isTablet) && (
                    <Box>
                      {paginatedOrders.map((order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          locationColor={LOCATION_COLOR}
                          onViewDetails={handleViewDetails}
                        />
                      ))}
                      
                      {filteredOrders.length > rowsPerPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <Pagination
                            count={Math.ceil(filteredOrders.length / rowsPerPage)}
                            page={page + 1}
                            onChange={(_, value) => setPage(value - 1)}
                            size={isMobile ? 'small' : 'medium'}
                            sx={{
                              '& .MuiPaginationItem-root': {
                                borderRadius: '8px',
                                fontWeight: 600,
                              },
                              '& .Mui-selected': {
                                bgcolor: `${LOCATION_COLOR} !important`,
                                color: 'white',
                                '&:hover': { bgcolor: `${LOCATION_COLOR} !important` },
                              },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </div>
        </div>
        <Footer />
      </div>

      {/* Order Detail Dialog */}
      <OrderDetailDialog
        open={detailDialogOpen}
        order={selectedOrder}
        onClose={handleCloseDetails}
        locationColor={LOCATION_COLOR}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          
          .MuiTableRow-root {
            transition: background-color 0.15s ease;
          }
          
          .MuiTableRow-root:hover {
            background-color: rgba(0,0,0,0.02);
          }
        `}
      </style>
    </>
  );
};

export default OrderHistory;